import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useConsent } from './consent';

/** Google Analytics 4 property; override per deployment with VITE_GA_ID ("" turns it off). */
const GA_ID: string = import.meta.env.VITE_GA_ID ?? 'G-47GDM57LT1';

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

function enabledHere() {
  return (
    !!GA_ID &&
    import.meta.env.PROD &&
    !['localhost', '127.0.0.1'].includes(window.location.hostname)
  );
}

/** Consent mode update: allow analytics storage. */
function grant() {
  window.gtag?.('consent', 'update', {
    analytics_storage: 'granted',
  });
}

/** Consent withdrawn or denied: stop analytics storage and remove any analytics cookies. */
function revoke() {
  window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, `.${host.split('.').slice(-2).join('.')}`];
  for (const c of document.cookie.split(';')) {
    const name = c.split('=')[0].trim();
    if (!/^_ga/.test(name)) continue;
    for (const d of domains)
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
  }
}

/**
 * Path without secrets: invitation and reset tokens never leave the browser, query strings
 * are dropped except the language.
 */
export function safePath(pathname: string, search: string): string {
  const path = pathname
    .replace(/^\/invite\/[^/]+/, '/invite/:token')
    .replace(/\/(templates|automations|batches)\/[^/]+/, '/$1/:id');
  const lang = new URLSearchParams(search).get('lang');
  return lang ? `${path}?lang=${lang}` : path;
}

/** Mount once inside the router: follows consent and reports page views. */
export function useAnalytics() {
  const allowed = useConsent((s) => !!s.consent?.analytics);
  const location = useLocation();
  useEffect(() => {
    if (!enabledHere()) return;
    if (allowed) grant();
    else revoke();
  }, [allowed]);
  useEffect(() => {
    if (!enabledHere() || !allowed) return;
    const page = safePath(location.pathname, location.search);
    window.gtag?.('event', 'page_view', {
      page_path: page,
      page_location: window.location.origin + page,
      page_title: document.title,
    });
  }, [allowed, location.pathname, location.search]);
}

/** A named event (no personal data in params). */
export function track(name: string, params: Record<string, string | number | boolean> = {}) {
  if (enabledHere() && useConsent.getState().consent?.analytics)
    window.gtag?.('event', name, params);
}
